import type { Stage as KonvaStage } from "konva/lib/Stage";
import type { ReactNode, Ref } from "react";

import type { PlanogramItem, PlanogramItemOrientation } from "@CommonTypes/merchflow/pog/item";
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import type { ShelfDeadSpan } from "@CommonUtils/pog/shelfMerchandising";
import type {
  PlanogramMoveCapability,
  PlanogramMoveCommit,
  PlanogramMoveResult,
  PlanogramMoveResultEvent,
  PlanogramOperation,
} from "./operations";
import type { OverlayChannelInput } from "./overlays";
import type { ViewportConfig, ViewportState } from "./viewport";
import type { FrontOverhangMarker } from "../../common/frontOverhangView";

export type PlanogramSnapshot = Pog;

export type PlanogramEntityKind =
  | "bay"
  | "shelf"
  | "product"
  | "pegboard"
  | "placement"
  | "container";

export interface PlanogramEntityRef {
  kind: PlanogramEntityKind;
  id: string;
}

export interface PlanogramContextRequest {
  readonly entity: PlanogramEntityRef;
  readonly selection: PlanogramSelection;
  /** Pointer coordinates are semantic menu placement data, not renderer events. */
  readonly position?: {
    readonly clientX: number;
    readonly clientY: number;
  };
}

export interface PlanogramOverlayBadgeRequest {
  readonly entityId: string;
  readonly position: {
    readonly clientX: number;
    readonly clientY: number;
  };
}

export interface PlanogramSelection {
  kind: PlanogramEntityKind | null;
  ids: readonly string[];
  anchorId: string | null;
  focusedId: string | null;
}

export const emptyPlanogramSelection: PlanogramSelection = {
  anchorId: null,
  focusedId: null,
  ids: [],
  kind: null,
};

export type PlanogramProductDisplayMode = "image" | "label";
/** A product field rendered as a label; kept generic so adapters can preserve domain label options. */
export type PlanogramProductLabel = keyof PlanogramItem | "none";

export interface PlanogramViewConfig {
  height?: number;
  offsetX?: number;
  offsetY?: number;
  productColors?: ReadonlyMap<string, string>;
  productDisplayMode?: PlanogramProductDisplayMode;
  productLabel?: PlanogramProductLabel;
  scale?: number;
  /** Draw shelf dividers; on unless a page turns them off. */
  showDividers?: boolean;
  /**
   * Draw the line between facings at a constant screen width, so a product's facings stay
   * countable without images. Off by default: the 1-unit world stroke is sub-pixel at fit scale.
   */
  outlineFacings?: boolean;
  width?: number;
}

export interface PlanogramProductAssetRequest {
  productCode: string;
  productId: string;
}

export interface PlanogramProductAsset {
  image: HTMLImageElement;
  alt?: string;
}

export interface PlanogramAssetProvider {
  loadProductImage: (
    request: PlanogramProductAssetRequest,
  ) => PlanogramProductAsset | null | Promise<PlanogramProductAsset | null>;
}

export interface PlanogramPolicy {
  /** Whether movement and resize affordances should be enabled for the adapter. */
  readonly canMove?: boolean;
  readonly canSelect?: (entity: PlanogramEntityRef) => boolean;
  readonly evaluate: (pog: Pog, operation: PlanogramOperation) => PlanogramMoveCapability;
  readonly execute: (args: {
    readonly operation: PlanogramOperation;
    readonly pog: Pog;
    readonly selection?: PlanogramSelection;
  }) => PlanogramMoveResult;
}

export interface PlanogramWorldRect {
  height: number;
  width: number;
  x: number;
  y: number;
}

export type PlanogramProductCellKind = "main" | "x-cap" | "y-cap";

export interface PlanogramProductCell extends PlanogramWorldRect {
  column: number;
  kind: PlanogramProductCellKind;
  orientation: PlanogramItemOrientation;
  row: number;
  unitsHigh: number;
  unitsWide: number;
}

export interface PlanogramSceneContainer extends PlanogramWorldRect {
  color: string;
  id: string;
  label: string;
  shelfType: "REGULAR" | "HANGCELL" | "BASKET";
}

export interface PlanogramSceneBay extends PlanogramWorldRect {
  baseHeight: number;
  bayNo: number;
  id: string;
  maxNotch: number;
  notchOffset: number;
  notchSpacing: number;
}

/**
 * A break in the run, sitting in the gap the projection leaves after a bay
 * whose `splitAfter` is set. The rect is the gap itself; the divider is drawn
 * down its middle.
 *
 * A split on the last bay never becomes one of these — the run already ends
 * there, so it says nothing.
 */
export interface PlanogramSceneSplit extends PlanogramWorldRect {
  /** The bay the run breaks after. */
  bayId: string;
  /** That bay's 1-based number. Bay numbers keep counting across a break. */
  bayNo: number;
  id: string;
}

/** A dead span worth flagging (see `getBoardDrawing`), with the room above it. */
export interface PlanogramSceneDeadSpan extends ShelfDeadSpan {
  /** Height of the zone above the strip, up to the lowest board live over it (or the bay top). */
  extensionHeight: number;
  /** Scene y of that zone. */
  extensionY: number;
}

/** Where a board is drawn and what of it is flagged; the board's own rect stays the full shelf. */
interface PlanogramSceneBoardDrawing {
  /** Shelf-relative x and width of the board as drawn: not where another board fills its dead span. */
  boardX: number;
  boardWidth: number;
  /** The dead spans drawn hatched: the ones no board beside it or rack over it fills. */
  flaggedDeadSpans: PlanogramSceneDeadSpan[];
  /** The rack this board is a tier of (its assembly ends with RACK), or null. */
  rackName: string | null;
  /** A rack board colliding with a board of its bay (see `getBayRacks`): drawn hatched. */
  rackCollides: boolean;
}

export interface PlanogramSceneShelf extends PlanogramWorldRect, PlanogramSceneBoardDrawing {
  frontOverhang?: FrontOverhangMarker | null;
  bayId: string;
  groupId: string;
  id: string;
  merchandisableWidth: number;
  /** Strips blocked by an obstruction, shelf-relative x (the hatched ones are `flaggedDeadSpans`). */
  deadSpans: ShelfDeadSpan[];
  /**
   * Height of the item-placement zone beyond the shelf's own thickness (up to the shelf above,
   * or bay top edge) — excludes the thickness band, which is covered by `height`/`y` instead.
   */
  nonMerchandisableExtensionHeight: number;
  /** Scene y of the extension zone (see nonMerchandisableExtensionHeight). */
  nonMerchandisableExtensionY: number;
  shelfNo: number;
  thickness: number;
}

export type PlanogramFixtureType = "RISER" | "HANGCELL";

export interface PlanogramSceneFixture extends PlanogramWorldRect, PlanogramSceneBoardDrawing {
  bayId: string;
  fixtureType: PlanogramFixtureType;
  id: string;
  merchandisableWidth: number;
  /** See PlanogramSceneShelf.deadSpans. */
  deadSpans: ShelfDeadSpan[];
  /**
   * Height of the item-placement zone beyond the fixture's own thickness: up to the shelf above
   * (or bay top edge) for RISER, down to the shelf below (or bay floor) for HANGCELL. For
   * HANGCELL this equals `height`/`y`, since those already exclude the peg-bar thickness.
   */
  nonMerchandisableExtensionHeight: number;
  /** Scene y of the extension zone (see nonMerchandisableExtensionHeight). */
  nonMerchandisableExtensionY: number;
  shelfNo: number;
  thickness: number;
}

export interface PlanogramSceneProduct extends PlanogramWorldRect {
  frontOverhang?: FrontOverhangMarker | null;
  cells: PlanogramProductCell[];
  horizontalFacings: number;
  id: string;
  isNewInsertedItem: boolean;
  isTray: boolean;
  label: string;
  name: string;
  orientation: PlanogramItemOrientation;
  productCode: string;
  shelfGroupId: string;
  shelfId: string;
  validOrientations: PlanogramItemOrientation[];
  verticalFacings: number;
}

export interface PlanogramSceneHole extends PlanogramWorldRect {
  column: number;
  row: number;
}

export interface PlanogramScenePegboard extends PlanogramWorldRect {
  bayId: string;
  holeGapHorizontal: number;
  holeGapVertical: number;
  holeOffsetHorizontal: number;
  holeOffsetVertical: number;
  holes: PlanogramSceneHole[];
  id: string;
  source: "planogram" | "syntheticHangcell";
  sourceShelfId: string | null;
}

export interface PlanogramScenePlacementCell extends PlanogramWorldRect {
  depth: number;
  holeNoLeft: number;
  holeNoTop: number;
}

export interface PlanogramScenePlacement extends PlanogramWorldRect {
  id: string;
  facingChangeColor: string | null;
  facingChangeType: string | null;
  facingCells: PlanogramScenePlacementCell[];
  label: string;
  name: string;
  pegboardId: string;
  productCode: string;
  productId: string;
}

/** A shelf divider, a thin slab standing on the shelf; absent from scenes without dividers. */
export interface PlanogramSceneDivider extends PlanogramWorldRect {
  id: string;
  shelfId: string;
}

/**
 * A rack drawn as one fixture: its world rect runs between its live edges, from
 * where it stands (its host's top, see `getBayRacks`) to the top of its top tier.
 */
export interface PlanogramSceneRack extends PlanogramWorldRect {
  bayId: string;
  /** `${bayId}:${key}`: a bay holds one rack per host. */
  id: string;
  name: string;
  boardIds: string[];
  /** The top tier's thickness: the band the rack's name is written in. */
  labelHeight: number;
}

export interface PlanogramScene {
  bays: PlanogramSceneBay[];
  containers: PlanogramSceneContainer[];
  dividers?: PlanogramSceneDivider[];
  fixtures: PlanogramSceneFixture[];
  height: number;
  pegboards?: PlanogramScenePegboard[];
  placements?: PlanogramScenePlacement[];
  products: PlanogramSceneProduct[];
  racks?: PlanogramSceneRack[];
  shelves: PlanogramSceneShelf[];
  /** One entry per break in the run, in bay order. Empty on an unbroken run. */
  splits: PlanogramSceneSplit[];
  unsupportedFixtureIds: string[];
  width: number;
}

/**
 * The seam handle on a store's fixtures: the one place an aisle break is
 * declared or joined. Every other canvas leaves this out and draws breaks
 * read-only.
 */
export interface PlanogramAisleBreakEditing {
  /** Break the aisle after this bay (true), or join it to the next bay again (false). */
  onSetSplitAfter: (bayId: string, splitAfter: boolean) => void;
  /** The hover tooltip on the break after this bay: who set it, or that the fixture file did. */
  describeBreak: (bayId: string) => string;
}

export interface PlanogramRendererLabels {
  /** The caption on the divider drawn at a break in the run. */
  readonly aisleBreak: string;
  /** The seam handle's pill between two bays that are one aisle. */
  readonly breakTheAisleHere: string;
  /** The same, for the keyboard, where the seam has to be named. */
  readonly breakTheAisleBetween: (leftBayNo: number, rightBayNo: number) => string;
  /** The seam handle's pill on a break: join the bay before it to the bay after it. */
  readonly joinBays: (leftBayNo: number, rightBayNo: number) => string;
  readonly bay: (bayNo: number) => string;
  readonly containers: string;
  readonly fixture: (fixtureType: PlanogramFixtureType, shelfNo: number) => string;
  readonly fixtures: string;
  readonly newItemIndicator: string;
  readonly pegboardFacingChangeIndicator: (changeType: string) => string;
  readonly moveLeft: string;
  readonly moveProductLeft: (label: string) => string;
  readonly moveProductRight: (label: string) => string;
  readonly moveRight: string;
  readonly pegboards: string;
  readonly placements: string;
  readonly products: string;
  readonly reset: string;
  readonly resetViewport: string;
  readonly resizeFixtureNarrower: (fixtureType: PlanogramFixtureType, shelfNo: number) => string;
  readonly resizeFixtureNarrowerText: string;
  readonly resizeFixtureWider: (fixtureType: PlanogramFixtureType, shelfNo: number) => string;
  readonly resizeFixtureWiderText: string;
  readonly resizeShelfNarrower: (shelfNo: number) => string;
  readonly resizeShelfNarrowerText: string;
  readonly resizeShelfWider: (shelfNo: number) => string;
  readonly resizeShelfWiderText: string;
  readonly selectContainer: (label: string) => string;
  readonly selectFixture: (fixtureType: PlanogramFixtureType, shelfNo: number) => string;
  readonly selectPegboard: (id: string) => string;
  readonly selectPlacement: (label: string) => string;
  readonly selectProduct: (label: string) => string;
  readonly selectShelf: (shelfNo: number) => string;
  readonly shelf: (shelfNo: number) => string;
  readonly shelves: string;
  readonly viewportControls: string;
  readonly zoomIn: string;
  readonly zoomOut: string;
}

/**
 * A translucent placeholder rect in world coordinates, drawn while an
 * external (panel) drag hovers the stage — the canvas equivalent of the DOM
 * Dropper's placeholder element.
 */
export type PlanogramDropPreview = PlanogramWorldRect;

export interface PlanogramWorldOverlayState {
  readonly shelfDragPreview: {
    readonly deltaY: number;
    readonly shelfIds: readonly string[];
  } | null;
}

export type PlanogramWorldOverlay = ReactNode | ((state: PlanogramWorldOverlayState) => ReactNode);

export interface KonvaPlanogramProps {
  centerOnFit?: boolean;
  /** Only the store fixtures editor passes this; without it breaks are drawn, never edited. */
  aisleBreakEditing?: PlanogramAisleBreakEditing;
  assetProvider?: PlanogramAssetProvider;
  /** Insertion-point ghost for an active external drag; null hides it. */
  dropPreview?: PlanogramDropPreview | null;
  labels: PlanogramRendererLabels;
  /** External presentation channels; they never affect POG or base hit testing. */
  overlayChannels?: readonly OverlayChannelInput[];
  /**
   * Short labels pinned to entities (entity id → text), such as numbered
   * violation badges. Their own presentation dimension: a badge never grants
   * emphasis, and no channel can erase one.
   */
  overlayBadges?: ReadonlyMap<string, string>;
  /** Activates an interactive overlay badge without selecting the entity beneath it. */
  onOverlayBadgeActivate?: (request: PlanogramOverlayBadgeRequest) => void;
  onActivate?: (entity: PlanogramEntityRef, selection?: PlanogramSelection) => void;
  /**
   * Fires once every product/placement image request for the current scene
   * has settled (or immediately when image loading is disabled) — the signal
   * rasterization hosts wait for before calling `stage.toDataURL()`.
   */
  onAssetsSettled?: () => void;
  onContextRequest?: (request: PlanogramContextRequest) => void;
  onCommit?: (commit: PlanogramMoveCommit) => void;
  /** Fires with the hovered entity, and with null when the pointer leaves it or the stage. */
  onHoverChange?: (entity: PlanogramEntityRef | null) => void;
  onMoveResult?: (event: PlanogramMoveResultEvent) => void;
  /**
   * Open with the whole wall in view, centred with a small margin, instead of at
   * `view.scale`. It refits while the canvas is still settling (a popup laying out)
   * and stops once the viewer zooms or pans; Reset returns to it.
   */
  openFitted?: boolean;
  onSelectionChange?: (selection: PlanogramSelection) => void;
  onViewportChange?: (viewport: ViewportState) => void;
  policy?: PlanogramPolicy;
  pog: PlanogramSnapshot;
  /**
   * Pans the viewport to center the entity when the id changes — the canvas
   * equivalent of the DOM renderer's scrollIntoView on the searched item.
   */
  scrollToEntityId?: string | null;
  selection?: PlanogramSelection;
  /** Konva stage handle, for rasterization hosts (`stage.toDataURL()`). */
  stageRef?: Ref<KonvaStage>;
  /** Non-interactive DOM decorations positioned in world coordinates. */
  worldOverlay?: PlanogramWorldOverlay;
  viewport?: ViewportState;
  viewportConfig?: ViewportConfig;
  view?: PlanogramViewConfig;
}
